import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  ACCOUNT_BRAND_DEFAULT,
  ACCOUNT_DASHBOARD_DEFAULTS_DEFAULT,
  ACCOUNT_DELIVERY_DEFAULT,
  ACCOUNT_GOALS_DEFAULT,
  ACCOUNT_PROFILES_SEED,
  GUEST_DATA_DEFAULT,
  RESPONSE_SETTINGS_DEFAULT,
  REVIEW_WIDGET_DEFAULT,
} from '@common/data';
import type {
  AccountBrand,
  AccountDashboardDefaults,
  AccountDeliverySettings,
  AccountDepartmentSettings,
  AccountGoals,
  AccountLifecycleState,
  AccountProfile,
  AccountTicketSettings,
  GuestDataSettings,
  ResponseSettings,
  ReviewWidgetConfig,
} from '@common/types';

import { UserPreferencesService } from './user-preferences.service';

const SAVE_LATENCY_MS = 320;

/** Per-account settings, keyed by account id. */
@Injectable({ providedIn: 'root' })
export class AccountSettingsService {
  private readonly preferences = inject(UserPreferencesService);

  private readonly profiles = signal<Record<string, AccountProfile>>({ ...ACCOUNT_PROFILES_SEED });
  private readonly brands = signal<Record<string, AccountBrand>>({});
  private readonly delivery = signal<Record<string, AccountDeliverySettings>>({});
  private readonly guestData = signal<Record<string, GuestDataSettings>>({});
  private readonly responses = signal<Record<string, ResponseSettings>>({});
  private readonly widgets = signal<Record<string, ReviewWidgetConfig>>({});
  private readonly goals = signal<Record<string, AccountGoals>>({});
  private readonly dashboardDefaults = signal<Record<string, AccountDashboardDefaults>>({});
  private readonly lifecycle = signal<Record<string, AccountLifecycleState>>({});
  private readonly transfers = signal<Record<string, string>>({});
  private readonly departments = signal<Record<string, AccountDepartmentSettings>>({});
  private readonly tickets = signal<Record<string, AccountTicketSettings>>({});

  departmentsFor(accountId: string): AccountDepartmentSettings {
    return this.departments()[accountId] ?? { accountId, disabledDepartmentIds: [], heads: {} };
  }

  saveDepartments(next: AccountDepartmentSettings): Observable<AccountDepartmentSettings> {
    this.departments.update((rows) => ({ ...rows, [next.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  ticketsFor(accountId: string): AccountTicketSettings {
    return (
      this.tickets()[accountId] ?? {
        accountId,
        defaultAssigneeId: null,
        escalationRecipientId: null,
        disabledAutoRuleIds: [],
      }
    );
  }

  saveTickets(next: AccountTicketSettings): Observable<AccountTicketSettings> {
    this.tickets.update((rows) => ({ ...rows, [next.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  profileFor(accountId: string): AccountProfile | null {
    return this.profiles()[accountId] ?? null;
  }

  brandFor(accountId: string): AccountBrand {
    return this.brands()[accountId] ?? { accountId, ...ACCOUNT_BRAND_DEFAULT };
  }

  deliveryFor(accountId: string): AccountDeliverySettings {
    return this.delivery()[accountId] ?? { accountId, ...ACCOUNT_DELIVERY_DEFAULT };
  }

  guestDataFor(accountId: string): GuestDataSettings {
    return this.guestData()[accountId] ?? { accountId, ...GUEST_DATA_DEFAULT };
  }

  responseSettingsFor(accountId: string): ResponseSettings {
    return this.responses()[accountId] ?? { accountId, ...RESPONSE_SETTINGS_DEFAULT };
  }

  widgetFor(accountId: string): ReviewWidgetConfig {
    return this.widgets()[accountId] ?? { accountId, ...REVIEW_WIDGET_DEFAULT };
  }

  goalsFor(accountId: string): AccountGoals {
    return this.goals()[accountId] ?? { accountId, ...ACCOUNT_GOALS_DEFAULT };
  }

  saveProfile(profile: AccountProfile): Observable<AccountProfile> {
    this.profiles.update((map) => ({ ...map, [profile.accountId]: profile }));
    return of(profile).pipe(delay(SAVE_LATENCY_MS));
  }

  saveBrand(brand: AccountBrand): Observable<AccountBrand> {
    this.brands.update((map) => ({ ...map, [brand.accountId]: brand }));
    return of(brand).pipe(delay(SAVE_LATENCY_MS));
  }

  saveDelivery(next: AccountDeliverySettings): Observable<AccountDeliverySettings> {
    this.delivery.update((map) => ({ ...map, [next.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  saveGuestData(next: GuestDataSettings): Observable<GuestDataSettings> {
    this.guestData.update((map) => ({ ...map, [next.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  saveResponseSettings(next: ResponseSettings): Observable<ResponseSettings> {
    this.responses.update((map) => ({ ...map, [next.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  saveWidget(next: ReviewWidgetConfig): Observable<ReviewWidgetConfig> {
    this.widgets.update((map) => ({ ...map, [next.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  saveGoals(next: AccountGoals): Observable<AccountGoals> {
    this.goals.update((map) => ({ ...map, [next.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  dashboardDefaultsFor(accountId: string): AccountDashboardDefaults {
    return (
      this.dashboardDefaults()[accountId] ?? { accountId, ...ACCOUNT_DASHBOARD_DEFAULTS_DEFAULT }
    );
  }

  saveDashboardDefaults(next: AccountDashboardDefaults): Observable<AccountDashboardDefaults> {
    const stamped: AccountDashboardDefaults = { ...next, updatedAt: new Date().toISOString() };
    this.dashboardDefaults.update((map) => ({ ...map, [next.accountId]: stamped }));
    return of(stamped).pipe(delay(SAVE_LATENCY_MS));
  }

  /**
   * Push the account default over everyone's personal arrangement. The browser cannot reach other
   * users' stored layouts, so what this does today is stamp the push and reset the signed-in
   * user's own dashboard - a real, visible effect rather than a toast reporting nothing.
   */
  resetDashboardsToDefault(accountId: string): Observable<AccountDashboardDefaults> {
    const current = this.dashboardDefaultsFor(accountId);
    const stamped: AccountDashboardDefaults = { ...current, lastResetAt: new Date().toISOString() };
    this.dashboardDefaults.update((map) => ({ ...map, [accountId]: stamped }));
    this.preferences.set('dashboardLayout', [...current.layout]);
    return of(stamped).pipe(delay(SAVE_LATENCY_MS));
  }

  lifecycleFor(accountId: string): AccountLifecycleState {
    return this.lifecycle()[accountId] ?? 'active';
  }

  /**
   * The danger-zone transitions. They go through the service like every other mutation so the
   * page has a failure path to report - a destructive action that can only ever succeed teaches
   * the user to trust a toast that means nothing.
   */
  setLifecycle(accountId: string, state: AccountLifecycleState): Observable<AccountLifecycleState> {
    this.lifecycle.update((map) => ({ ...map, [accountId]: state }));
    return of(state).pipe(delay(SAVE_LATENCY_MS));
  }

  transferredOrganizationFor(accountId: string): string | null {
    return this.transfers()[accountId] ?? null;
  }

  transferAccount(accountId: string, organizationId: string): Observable<string> {
    this.transfers.update((map) => ({ ...map, [accountId]: organizationId }));
    return of(organizationId).pipe(delay(SAVE_LATENCY_MS));
  }
}
